// Per-room link rules, mirrored from the backend.
//
// The rule is enforced in
// Backend/supabase/functions/plenty-of-hearts/services/Community/linkPolicy.ts.
// This copy exists so someone writing a post in the jobs room is told before
// they hit Post, rather than after a round trip — the client is the courtesy,
// the server is the rule. The two files must be changed together; if they ever
// disagree, the server wins and the user sees its message.
//
// Rooms are open to links by default. Jobs Available is the exception: it is a
// professional board, so links there must point at LinkedIn.

const LINKEDIN_ONLY_SLUGS = new Set(['jobs-available']);

// A curated TLD list rather than "any dot-separated word", which would read
// "Node.js" or a missing space after a full stop as a link.
const BARE_DOMAIN_TLDS = [
  'com', 'net', 'org', 'io', 'co', 'dev', 'ai', 'app', 'me', 'us', 'uk', 'ca',
  'info', 'biz', 'xyz', 'online', 'site', 'club', 'shop', 'live', 'work',
  'jobs', 'careers', 'agency', 'link', 'to', 'ly', 'gg', 'tv',
].join('|');

const URL_PATTERNS = [
  /\bhttps?:\/\/\S+/gi,
  /\bwww\.\S+/gi,
  new RegExp(`\\b[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\\.[a-z0-9-]+)*\\.(?:${BARE_DOMAIN_TLDS})\\b(?:\\/\\S*)?`, 'gi'),
];

const TRAILING_PUNCTUATION = /[.,;:!?)\]}'"»]+$/;

export function extractUrls(text: string): string[] {
  if (!text) return [];
  const found = new Set<string>();
  for (const pattern of URL_PATTERNS) {
    const matches = text.match(pattern) ?? [];
    for (const match of matches) {
      const cleaned = match.replace(TRAILING_PUNCTUATION, '');
      if (cleaned) found.add(cleaned);
    }
  }
  return Array.from(found);
}

// Parsed, not pattern-matched: `linkedin.com.scam.ru`,
// `https://linkedin.com@evil.tld/` and `evil.tld/linkedin.com` all contain the
// string "linkedin.com" and none of them are LinkedIn.
function hostOf(url: string): string | null {
  try {
    const withScheme = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    return new URL(withScheme).hostname.toLowerCase().replace(/^www\./, '');
  } catch {
    return null;
  }
}

function isLinkedIn(host: string): boolean {
  return host === 'linkedin.com' || host.endsWith('.linkedin.com');
}

/** The first disallowed link in `text`, or null when it is allowed. */
export function checkLinkPolicy(communitySlug: string, text: string): string | null {
  if (!LINKEDIN_ONLY_SLUGS.has(communitySlug)) return null;

  for (const url of extractUrls(text)) {
    const host = hostOf(url);

    if (!host) {
      return `"${url}" could not be read as a link. Only LinkedIn links can be shared in this room.`;
    }

    // LinkedIn's own shortener still hides its destination — it forwards to
    // whatever was shared through LinkedIn, which can be any site.
    if (host === 'lnkd.in') {
      return 'Shortened lnkd.in links hide where they lead, so they cannot be shared here. Post the full linkedin.com URL instead.';
    }

    if (!isLinkedIn(host)) {
      return `Only LinkedIn links can be shared in Jobs Available. "${host}" is not a LinkedIn address — link to the LinkedIn job or profile instead.`;
    }
  }

  return null;
}

/** True when the room restricts links, so callers can show a hint up front. */
export function roomRestrictsLinks(communitySlug: string): boolean {
  return LINKEDIN_ONLY_SLUGS.has(communitySlug);
}
